import { motion } from "framer-motion";
import { fadeUp, revealScale, viewport } from "@/lib/motion";
import { IMAGES } from "@/lib/data";

const CHAPTERS = [
  { num: "01", title: "Purpose", body: "Every organization begins with a calling. We help you name it clearly and build around it with intention and integrity." },
  { num: "02", title: "Strategy", body: "Purpose without a plan stalls. We translate your mission into clear, fundable strategy—research, readiness, and roadmaps." },
  { num: "03", title: "Impact", body: "Strategy is only as good as its execution. We stand beside your leadership to turn plans into measurable, lasting change." },
];

export const About = () => {
  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left: intro + image */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}
              className="text-xs font-semibold tracking-[0.25em] uppercase text-gold mb-6">
              About Faith &amp; Grace
            </motion.p>
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} custom={1}
              className="font-serif font-light text-4xl sm:text-5xl leading-tight text-primary mb-8">
              A trusted partner for organizations built to endure.
            </motion.h2>
            <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} custom={2}
              className="text-base md:text-lg font-light leading-relaxed text-muted-foreground mb-10">
              Faith &amp; Grace Advisory Services exists to strengthen the organizations that strengthen communities. With expertise across grants, governance, and growth, we walk alongside leaders—guiding them from vision to sustainable impact.
            </motion.p>
            <motion.div variants={revealScale} initial="hidden" whileInView="show" viewport={viewport}
              className="relative aspect-[4/5] overflow-hidden"
              style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 0 100%)" }}>
              <img src={IMAGES.community} alt="Community impact" className="h-full w-full object-cover" />
            </motion.div>
          </div>

          {/* Right: numbered manifesto */}
          <div className="lg:col-span-7 lg:pt-4">
            <div className="border-t border-border">
              {CHAPTERS.map((c, i) => (
                <motion.div key={c.num} variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} custom={i}
                  className="grid grid-cols-[auto_1fr] gap-6 md:gap-12 py-10 md:py-14 border-b border-border">
                  <span className="font-serif text-4xl md:text-6xl font-light text-gold leading-none">{c.num}</span>
                  <div>
                    <h3 className="font-serif text-3xl md:text-4xl font-normal text-primary mb-4">{c.title}</h3>
                    <p className="text-base md:text-lg font-light leading-relaxed text-muted-foreground max-w-xl">{c.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Mission callout */}
            <motion.div id="mission" variants={revealScale} initial="hidden" whileInView="show" viewport={viewport}
              className="mt-14 bg-navy text-[hsl(var(--background))] p-10 md:p-14 relative overflow-hidden">
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold mb-6">Our Mission</p>
              <p className="font-serif text-2xl md:text-3xl font-light leading-snug">
                To equip nonprofits, ministries, and small businesses with the strategy, funding, and leadership they need to create <span className="italic text-gold">lasting community impact</span>—with excellence, integrity, and grace.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
